import { routes } from "@constants/routes";
import { handleApiRequest, LogoIcon, Typography } from "@cpj-ai/panel-npm-package";
import { Button } from "@heroui/react";

import confetti from "canvas-confetti";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  UpdateOnboardingValuesMutation,
  UpdateOnboardingValuesMutationVariables,
  useUpdateOnboardingValuesMutation,
} from "@graphql/generated";
import { useProfile } from "@hooks/useProfile";
import CountrySelector from "@modules/Onboarding/Country/CountrySelector";
import Illustration from "@modules/Onboarding/Illustration";
import IndustrySelector from "@modules/Onboarding/Industry/IndustrySelector";
import JobInterestSelector from "@modules/Onboarding/JobInterest/JobInterestSelector";
import StepIndicator from "@modules/Onboarding/StepIndicator";
import SuccessStep from "@modules/Onboarding/Success/SuccessStep";

const ALL_STEPS = [
  { slug: "industry", title: "What industry are you in?", type: "industry" },
  { slug: "jobInterest", title: "What job are you interested in?", type: "job" },
  { slug: "country", title: "Where do you currently live?", type: "country" },
  { slug: "success", title: "All set! You're ready to go", type: "success" },
] as const;

const Onboarding = () => {
  const navigate = useNavigate();

  const { fetchProfile, loading, profile } = useProfile();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    industry: "",
    country: "",
    jobInterest: [] as string[],
  });

  const hasProfileLoadedRef = useRef(false);
  useEffect(() => {
    if (profile && Object.keys(profile).length > 0 && !hasProfileLoadedRef.current) {
      hasProfileLoadedRef.current = true;

      setFormData((current) => ({
        industry: profile.industry || current.industry,
        country: profile.countryId || current.country,
        jobInterest:
          profile.interestedJobs && profile.interestedJobs.length > 0 ? profile.interestedJobs : current.jobInterest,
      }));
    }
  }, [profile]);

  const existingUserData = useMemo(
    () => ({
      industry: "",
      jobInterest: [],
      country: "",
    }),
    [profile],
  );

  const neededSteps = useMemo(() => {
    const steps = [];

    if (!existingUserData.industry) {
      steps.push(ALL_STEPS.find((s) => s.slug === "industry")!);
    }

    if (
      !existingUserData.jobInterest ||
      (Array.isArray(existingUserData.jobInterest) && existingUserData.jobInterest.length === 0)
    ) {
      steps.push(ALL_STEPS.find((s) => s.slug === "jobInterest")!);
    }

    if (!existingUserData.country) {
      steps.push(ALL_STEPS.find((s) => s.slug === "country")!);
    }
    steps.push(ALL_STEPS.find((s) => s.slug === "success")!);

    return steps;
  }, [existingUserData]);

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const [updateOnboardingValues, { loading: updateOnboardingValuesLoading }] = useUpdateOnboardingValuesMutation();

  const currentStep = neededSteps[currentStepIndex];
  const totalSteps = neededSteps.length;

  useEffect(() => {
    if (currentStep?.slug === "success") {
      const fireConfetti = () => {
        const end = Date.now() + 1000;

        const createBurst = () => {
          const position = {
            x: 0.2 + Math.random() * 0.8,
            y: 0.2 + Math.random() * 0.2,
          };

          const colors = ["#C71F2C", "#f44336", "#e91e63", "#FFCDD2", "#FFA000", "#4CAF50", "#2196F3"];

          confetti({
            particleCount: 10 + Math.floor(Math.random() * 40),
            spread: 20 + Math.random() * 40,
            origin: position,
            colors: colors,
            ticks: 300,
            gravity: 1.0,
            scalar: 1.2,
            shapes: ["circle", "square"],
            zIndex: 1000,
          });
        };

        confetti({
          particleCount: 100,
          spread: 100,
          origin: { x: 0.5, y: 0.5 },
          colors: ["#C71F2C", "#FFCDD2", "#FFA000", "#4CAF50", "#2196F3"],
          ticks: 400,
          gravity: 0.8,
          decay: 0.9,
          shapes: ["circle", "square"],
          zIndex: 1000,
        });

        const intervalId = setInterval(() => {
          const now = Date.now();

          if (now > end) {
            return clearInterval(intervalId);
          }

          createBurst();

          setTimeout(createBurst, 200);
        }, 500);

        return () => clearInterval(intervalId);
      };

      const cleanup = fireConfetti();
      return cleanup;
    }
    return undefined;
  }, [currentStep]);

  const handleSelectionWithAdvance = (field: string, value: string | string[]) => {
    setFormData({ ...formData, [field]: value });

    if (field !== "jobInterest") {
      setTimeout(() => {
        goToNextStep();
      }, 500);
    }
  };

  const goToNextStep = () => {
    if (currentStepIndex < totalSteps - 1) {
      setDirection(1);
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      goToLogin();
    }
  };

  const goToPreviousStep = () => {
    if (currentStepIndex > 0) {
      setDirection(-1);
      setCurrentStepIndex(currentStepIndex - 1);
    }
  };

  const pageVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 50 : -50,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 50 : -50,
      opacity: 0,
    }),
  };

  const pageTransition = {
    type: "tween",
    ease: "easeInOut",
    duration: 0.3,
  };

  const goToLogin = () => {
    navigate(routes.auth.main);
  };

  const renderStepContent = () => {
    if (!currentStep) return null;

    switch (currentStep.slug) {
      case "industry":
        return (
          <IndustrySelector
            selectedIndustry={formData.industry}
            onSelect={(value) => handleSelectionWithAdvance("industry", value)}
            scrollContainerRef={scrollContainerRef}
          />
        );

      case "jobInterest":
        return (
          <JobInterestSelector
            selectedJobInterest={formData.jobInterest}
            onSelect={(value) => handleSelectionWithAdvance("jobInterest", value)}
            scrollContainerRef={scrollContainerRef}
          />
        );

      case "country":
        return (
          <CountrySelector
            selectedCountry={formData.country}
            onSelect={(value) => handleSelectionWithAdvance("country", value)}
            scrollContainerRef={scrollContainerRef}
          />
        );

      case "success":
        return (
          <SuccessStep
            handleContinue={handleContinue}
            existingUserData={existingUserData}
            isLoadingContinue={updateOnboardingValuesLoading}
            formData={formData}
          />
        );

      default:
        return null;
    }
  };

  const handleContinue = async () => {
    if (currentStep?.slug === "success") {
      await handleApiRequest<UpdateOnboardingValuesMutation, UpdateOnboardingValuesMutationVariables>({
        apiFunction: updateOnboardingValues,
        variables: {
          country: formData.country,
          industry: formData.industry,
          interestedJobs: formData.jobInterest,
        },
        onSuccess: () => {
          fetchProfile();
        },
        componentName: "OnboardingPage",
        actionName: "updateOnboardingValues",
        successMessage: "Onboarding values updated successfully",
        fallbackErrorMessage: "Failed to update onboarding values",
      });
    } else {
      goToNextStep();
    }
  };

  const isStepValid = () => {
    if (!currentStep) return false;

    switch (currentStep.slug) {
      case "industry":
        return !!formData.industry;
      case "jobInterest":
        return formData.jobInterest && Array.isArray(formData.jobInterest) && formData.jobInterest.length > 0;
      case "country":
        return !!formData.country;
      case "success":
        return true;
      default:
        return false;
    }
  };

  const shouldShowBackButton = () => {
    return currentStepIndex > 0 && currentStep?.slug !== "success";
  };

  const shouldShowContinueButton = () => {
    return currentStep?.slug === "jobInterest" || currentStep?.slug === "success";
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-3 md:px-4 p-4 overflow-x-hidden relative">
      <div className="w-full md:max-w-7xl h-[95vh] md:h-[770px] md:bg-white md:rounded-xl md:shadow-lg flex overflow-hidden relative z-10">
        <div className="w-full md:w-3/5 md:p-10 flex flex-col overflow-hidden">
          <div className="flex gap-4 items-center mb-6">
            <LogoIcon width={52} height={48} className="fill-primary-500" />
            <Typography className="text-primary-500 font-medium">Canada Prime Jobs</Typography>
          </div>

          <StepIndicator currentStep={currentStepIndex + 1} totalSteps={totalSteps} />

          <div ref={scrollContainerRef} className="flex-grow pr-2 overflow-y-auto overflow-x-hidden">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentStepIndex}
                custom={direction}
                variants={pageVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={pageTransition}
                className="overflow-x-hidden md:pr-2"
              >
                {renderStepContent()}
              </motion.div>
            </AnimatePresence>
          </div>

          <div
            className={`mt-auto pt-6 flex flex-col-reverse gap-3 md:flex-row items-center w-full justify-center ${currentStep?.slug === "success" && "justify-end"} ${currentStep?.slug === "jobInterest" && "justify-between"}`}
          >
            <Button
              color="default"
              variant="light"
              className={`text-sm md:text-base w-full ${currentStep?.slug === "jobInterest" && "md:!w-auto"} ${
                !shouldShowBackButton()
                  ? "hidden"
                  : "group text-sm md:text-base relative overflow-hidden font-medium transition-all hover:-translate-y-1"
              }`}
              onPress={goToPreviousStep}
              startContent={
                <svg
                  className="w-4 h-4 md:w-5 md:h-5 mr-2 group-hover:-translate-x-1 transition-transform"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              }
            >
              Back
            </Button>

            {shouldShowContinueButton() && (
              <Button
                color="primary"
                className={`text-sm md:text-base w-full md:w-auto font-medium px-10 py-3.5 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 transform group relative overflow-hidden self-end ${
                  currentStep?.slug === "success" ? "!w-full" : ""
                }`}
                onPress={handleContinue}
                endContent={
                  <svg
                    className="w-4 h-4 md:w-5 md:h-5 ml-2 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                }
                isLoading={updateOnboardingValuesLoading || loading}
                isDisabled={!isStepValid()}
              >
                Continue
              </Button>
            )}
          </div>
        </div>

        <div className="w-2/5 bg-lime-100 relative hidden md:block">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStepIndex}
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Illustration type={currentStep?.type as any} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
